// src/types/errors.ts
import { z } from 'zod/v4';

export const infinexErrorTypeSchema = z.enum([
  'NEEDS_CREDENTIALS',
  'NEEDS_APPROVAL',
  'ACTION_DENIED',
  'CONNECT_DISABLED',
  /**
   * The signing provider's policy refused to sign with the requested key
   * (e.g. a legacy Turnkey policy that only allows the operation wallet).
   * Retrying can never succeed - the account's policies need an upgrade.
   */
  'SIGNING_POLICY_DENIED',
  /**
   * The wallet's security settings block dapp signing
   * (user_wallet.dapp_signing_disabled). Retrying cannot succeed —
   * the user must unblock the wallet in Security settings.
   */
  'DAPP_SIGNING_DISABLED',
  'UNKNOWN',
]);
export type InfinexErrorType = z.infer<typeof infinexErrorTypeSchema>;

export const credentialTypeSchema = z.enum([
  'ACCOUNT_TOKEN',
  'SIGN_TOKEN',
  'CONNECT_SESSION',
]);
export type CredentialType = z.infer<typeof credentialTypeSchema>;

/** Details for when an action needs explicit approval */
export const approvalSchema = z
  .object({
    method: z.enum(['passkey', '2FA']),
    txnRequestId: z.string(),
  })
  .strict();
export type ApprovalError = z.infer<typeof approvalSchema>;

export const infinexErrorResponseSchema = z.object({
  type: infinexErrorTypeSchema,

  /** human-friendly message */
  message: z.string().optional(),

  /** set to `true` when the action is outright denied */
  actionDenied: z.boolean().optional(),

  /** present when the action needs a higher-level approval */
  needsApproval: approvalSchema.optional(),

  /** present when one or more credentials are missing */
  missingCredentials: z.array(credentialTypeSchema).optional(),

  /** arbitrary extra data */
  data: z.record(z.string(), z.any()).optional(),
});
export type InfinexErrorResponse = z.infer<typeof infinexErrorResponseSchema>;

export interface ErrorEnvelope {
  /** Marker so deserialize() can tell it’s ours. */
  __err: 1;
  /** Original constructor name, e.g. "UserRejectedRequestError". */
  name: string;
  /** err.message */
  message: string;
  /** err.stack (optional – browsers omit it in some cases). */
  stack?: string;
  /** All own enumerable properties (code, data, metaMessages, etc.). */
  props?: Record<string, unknown>;
  /** Recursive cause chain. */
  cause?: ErrorEnvelope;
}

export const errorEnvelopeSchema: z.ZodSchema<ErrorEnvelope> = z.lazy<
  z.ZodSchema<ErrorEnvelope>
>(() =>
  z.object({
    __err: z.literal(1),
    name: z.string(),
    message: z.string(),
    stack: z.string().optional(),
    props: z.record(z.string(), z.unknown()).optional(),
    cause: errorEnvelopeSchema.optional(),
  })
);
